import { Check, X } from "lucide-react";

import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card, Chip, ArrowLink } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { LabistaChat } from "../components/LabistaChat";
import {
  PrintDocFrame,
  PrintButton,
  PrintColophon,
} from "../components/PrintableDoc";
import { CTASection } from "../sections/CTASection";
import { productBySlug } from "../data/products";

/**
 * /labista — the Agent Labista product page.
 *
 * This page exists to fix an asymmetry. Hazri Book had a product page here
 * and kept its subdomain for the app; Agent Labista had no page here at all,
 * so "Explore Agent Labista" threw the visitor onto a second marketing site
 * on a different domain, with a different nav, and no way back. A prospect
 * comparing the two products had to leave the company site to see one of
 * them.
 *
 * Structure deliberately mirrors /hazribook: the problem, what the product
 * does, what it REFUSES to do, who it is for, and how you start. The refusal
 * section is the one that sells: any vendor can claim an AI answers
 * questions, and the thing a builder is actually afraid of is it answering
 * the wrong one in writing.
 */

const PROBLEM = [
  {
    t: "The enquiry arrives at 11pm",
    b: "Buyers browse after work, on their phone, in whatever language they think in. Your sales floor is closed, and the auto-reply that says 'we will get back to you' is the moment the lead cools.",
  },
  {
    t: "The reply decides the sale, not the project",
    b: "By the time someone calls back, the buyer has messaged three other towers. The lead was not lost to a better building — it was lost to a faster answer.",
  },
  {
    t: "Your best people spend the day qualifying",
    b: "Most of a sales team's hours go to enquiries that were never going to buy: wrong budget, wrong city, wrong decade. The ones that were ready waited behind them.",
  },
];

const DOES = [
  {
    n: "01",
    t: "Answers from your own documents",
    b: "Brochures, price sheets, floor plans, FAQs. The reply cites the source it came from, so a buyer can see where the number came from and your sales head can check it.",
  },
  {
    n: "02",
    t: "Captures the lead inside the conversation",
    b: "Name, area, budget, timeline, how they want to be contacted — gathered across a normal exchange, not extracted through a form nobody fills in.",
  },
  {
    n: "03",
    t: "Books the site visit",
    b: "Into the sales team's calendar, at a time the buyer picked, with the whole transcript attached so whoever takes the visit already knows the conversation.",
  },
  {
    n: "04",
    t: "Hands over warm, and tagged",
    b: "Scored, routed, and attributed — direct, channel partner, micro-site or paid ad — so nobody's commission goes missing and nobody gets called twice.",
  },
];

/** The refusals. This is the section a builder's legal team reads. */
const REFUSES = [
  "Invent a price that is not in your price sheet",
  "Guess a possession date",
  "State a carpet area it cannot cite",
  "Produce a RERA number from memory",
  "Keep talking when it does not know the answer",
];

const DOES_INSTEAD = [
  "Says plainly that it does not have that detail",
  "Hands the conversation to a human, with context",
  "Flags the gap so you can upload what was missing",
];

const LANGUAGES = [
  { lang: "Hindi", script: "Devanagari", line: "नमस्ते, वर्ली में 3BHK चाहिए, बजट 2.5 करोड़" },
  { lang: "Hinglish", script: "Roman", line: "Hi, Worli mein 3BHK chahiye, budget around 2.5 cr" },
  { lang: "Marathi", script: "Devanagari", line: "नमस्कार, वरळीत 3BHK हवा, बजेट साधारण 2.5 कोटी" },
  { lang: "Arabic", script: "Right-to-left", line: "مرحبا، أبحث عن شقة ٣ غرف في دبي مارينا" },
];

const FOR_SALES = [
  {
    t: "A lead board, not an inbox",
    b: "Filter by project, score, language, source or last activity. Reassign by dragging. The queue is ordered by who is closest to buying, not by who messaged most recently.",
  },
  {
    t: "Hot leads page a human",
    b: "When a buyer crosses the threshold you set, the right associate's phone buzzes while the buyer is still typing.",
  },
  {
    t: "Attribution that survives",
    b: "Channel partners keep their tagging and their commission flow. The agent sits in front of your funnel, it does not cannibalise it.",
  },
  {
    t: "Every conversation searchable",
    b: "Find each buyer who asked about a 2027 possession, across every project, in one query. Exportable, and audit-logged for DPDP.",
  },
];

const FAQ = [
  {
    q: "Does it run on our own WhatsApp number?",
    a: "Yes. It connects to your WhatsApp Business number, so buyers see your brand rather than a third-party bot. We handle the Meta Business verification and template approval.",
  },
  {
    q: "What happens when it does not know something?",
    a: "It says so and hands the conversation to a human, with the transcript attached. It is built to refuse rather than to guess, because a confidently wrong possession date is a legal problem, not a support ticket.",
  },
  {
    q: "Will it confuse our channel partners?",
    a: "No. Every lead is tagged by source and routed the way you configure it. CP-originated leads stay attributed to the CP.",
  },
  {
    q: "What about data protection?",
    a: "Consent capture, purpose tagging, deletion workflows and audit logs are built in rather than added later, aligned to India's DPDP regime.",
  },
  {
    q: "How long does it take to go live?",
    a: "The work on your side is uploading your project collateral. Most of the elapsed time is Meta's verification, not our setup.",
  },
];

export function Labista() {
  const product = productBySlug("agent-labista");

  return (
    <>
      <PrintDocFrame
        docTitle="Agent Labista"
        subtitle="AI sales agent for real estate — product brochure"
      />

      <PageHero
        eyebrow="Agent Labista · Real estate"
        title={
          <>
            Answer the enquiry{" "}
            <span className="text-gradient">before your competitor picks up.</span>
          </>
        }
        lede="An AI sales agent that lives on your WhatsApp number, answers buyers from your own brochures in their own language, qualifies them inside the conversation, and books the site visit. It is in development — we are looking for builders to run the first live deployments with."
        primary={{ href: "/contact", label: "Talk to us about a pilot" }}
        secondary={{ href: "#how", label: "See how it works" }}
        aside={<LabistaChat />}
      />

      <PrintButton />

      <Section id="problem">
        <div className="container">
          <SectionHead
            eyebrow="The problem"
            title={
              <>
                Property leads do not die from lack of interest.{" "}
                <span className="text-gradient">They die waiting.</span>
              </>
            }
          />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {PROBLEM.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.07}>
                <Card className="h-full p-6 sm:p-7">
                  <h3 className="font-display text-base font-semibold leading-snug tracking-tight">
                    {p.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">{p.b}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="how">
        <div className="container">
          <SectionHead
            eyebrow="What it does"
            title={
              <>
                Four things, on every enquiry,{" "}
                <span className="text-gradient">at any hour.</span>
              </>
            }
            lede="Not a chatbot that deflects. An associate that finishes the job a junior would have done, on the channel the buyer already opened."
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {DOES.map((d, i) => (
              <Reveal key={d.n} delay={(i % 2) * 0.07}>
                <Card interactive accent="violet" className="h-full p-6 sm:p-7">
                  <div className="font-mono text-[11px] tabular-nums text-white/25">
                    {d.n}
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold tracking-tight">
                    {d.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">{d.b}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* The section that actually closes the deal. */}
      <Section id="refuses" tight>
        <div className="container">
          <SectionHead
            eyebrow="What it refuses to do"
            title={
              <>
                The useful thing about this agent is{" "}
                <span className="text-gradient">what it will not say.</span>
              </>
            }
            lede="Anyone can ship an AI that answers. The risk a builder actually carries is one that answers wrongly, in writing, to a buyer who screenshots it."
          />

          <div className="mt-10 grid gap-4 lg:grid-cols-2">
            <Reveal>
              <Card className="h-full p-6 sm:p-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-300/80">
                  It will never
                </div>
                <ul className="mt-5 space-y-3">
                  {REFUSES.map((r) => (
                    <li key={r} className="flex gap-3 text-sm leading-relaxed text-white/75">
                      <X className="mt-0.5 size-4 shrink-0 text-rose-400" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Reveal>

            <Reveal delay={0.08}>
              <Card className="h-full p-6 sm:p-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-300/90">
                  It does this instead
                </div>
                <ul className="mt-5 space-y-3">
                  {DOES_INSTEAD.map((r) => (
                    <li key={r} className="flex gap-3 text-sm leading-relaxed text-white/75">
                      <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 border-t border-white/[0.08] pt-5 text-sm leading-relaxed text-white/55">
                  Every answer is grounded in the documents you uploaded and cites
                  the one it came from. If the answer is not in your material, there
                  is no answer to give.
                </p>
              </Card>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section id="languages">
        <div className="container">
          <SectionHead
            eyebrow="In the buyer's own language"
            title={
              <>
                Eight languages,{" "}
                <span className="text-gradient">matched rather than translated.</span>
              </>
            }
            lede="The agent holds the buyer's script and register across the whole conversation — Devanagari stays Devanagari, Hinglish does not turn into textbook Hindi, and Arabic runs right to left."
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {LANGUAGES.map((l, i) => (
              <Reveal key={l.lang} delay={(i % 2) * 0.06}>
                <Card className="h-full p-5 sm:p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold text-white">{l.lang}</span>
                    <span className="text-[11px] uppercase tracking-wider text-white/35">
                      {l.script}
                    </span>
                  </div>
                  <p
                    dir={l.script === "Right-to-left" ? "rtl" : "ltr"}
                    className="mt-3 rounded-xl bg-white/[0.05] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/85"
                  >
                    {l.line}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
          {product ? (
            <Reveal delay={0.12}>
              <div className="mt-6 flex flex-wrap gap-1.5">
                {["English", "Hindi", "Hinglish", "Arabic", "Marathi", "Tamil", "Telugu", "Gujarati"].map(
                  (l) => (
                    <Chip key={l}>{l}</Chip>
                  ),
                )}
              </div>
            </Reveal>
          ) : null}
        </div>
      </Section>

      <Section id="sales-team" tight>
        <div className="container">
          <SectionHead
            eyebrow="For the sales team"
            title={
              <>
                The agent is the front door.{" "}
                <span className="text-gradient">This is the room behind it.</span>
              </>
            }
          />
          <div className="mt-10 grid overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] sm:grid-cols-2">
            {FOR_SALES.map((f, i) => (
              <Reveal
                key={f.t}
                delay={(i % 2) * 0.06}
                className="border-b border-r border-white/[0.08] p-6 sm:p-7"
              >
                <h3 className="font-display text-base font-semibold tracking-tight text-white">
                  {f.t}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/60">{f.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="faq">
        <div className="container">
          <SectionHead
            eyebrow="What builders ask first"
            title={
              <>
                The five questions{" "}
                <span className="text-gradient">every demo starts with.</span>
              </>
            }
          />
          <div className="mt-10 max-w-3xl divide-y divide-white/[0.08] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="p-6">
                  <h3 className="font-display text-base font-semibold tracking-tight text-white">
                    {f.q}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-8">
              <ArrowLink href="/products">See our other product, Hazri Book</ArrowLink>
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection
        title={
          <>
            Bring one project and{" "}
            <span className="text-gradient">its real brochure.</span>
          </>
        }
        lede="We will run the demo on your own documents, in the languages your buyers actually message in, and you can try to catch it out. We are open about where this is: the product works and is not yet live with a builder, so the first deployments will be worked through closely with us."
        primary={{ href: "/contact", label: "Talk to us about a pilot" }}
        secondary={{ href: "/work", label: "See our work" }}
      />

      <PrintColophon />
    </>
  );
}
